import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatter, Task } from '@repo/shared';
import { getRouteApi } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';

import { useClusterById } from '@/api/generated';
import type {
  DescribeImageModelRun,
  ExtractStructureModelRun,
  GenerateAudioModelRun,
  TextTranslationModelRun,
} from '@/api/generated.schemas';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { columns as basicColumns } from '@/routes/(app)/(clusters)/components/basic-columns';
import OverallScore from '@/routes/(app)/(clusters)/components/overall-score';
import { columns as sharedColumns } from '@/routes/(app)/(clusters)/components/shared-columns';
import { columns as audioGenerationColumns } from '@/routes/(app)/(model-runs)/components/audio-generation-columns';
import { columns as describeImageColumns } from '@/routes/(app)/(model-runs)/components/describe-image-columns';
import { columns as extractStructureColumns } from '@/routes/(app)/(model-runs)/components/extract-structure-columns';
import { columns as translateColumns } from '@/routes/(app)/(model-runs)/components/text-translation-columns';
import ModelRuns from '@/routes/(app)/(model-runs)/model-runs';

const route = getRouteApi('/app/clusters/$clusterId');

type ModelRun =
  | TextTranslationModelRun
  | GenerateAudioModelRun
  | DescribeImageModelRun
  | ExtractStructureModelRun;

export const ClusterDetails = () => {
  const { clusterId } = route.useParams();
  const { t } = useTranslation();

  const { data, isLoading } = useClusterById(clusterId);

  const isEval = useMemo(() => data?.dataset.isEval, [data?.dataset]);

  const columns: Array<ColumnDef<ModelRun>> = useMemo(() => {
    if (!data) return [];
    if (!isEval) {
      return [...basicColumns, ...sharedColumns];
    }
    switch (data.dataset.task) {
      case Task.GENERATE_AUDIO:
        return [...audioGenerationColumns, ...sharedColumns];
      case Task.TEXT_TRANSLATION:
        return [...translateColumns, ...sharedColumns];
      case Task.DESCRIBE_IMAGE:
        return [...describeImageColumns, ...sharedColumns];
      case Task.EXTRACT_STRUCTURE:
        return [...extractStructureColumns, ...sharedColumns];
      default:
        throw new Error('Unsupported task');
    }
  }, [data, isEval]) as Array<ColumnDef<ModelRun>>;

  if (isLoading || !data) {
    return <Spinner />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('overview.title', {
              dataset: data.dataset.name,
              model: data.cluster.model,
            })}
          </CardTitle>
          <CardDescription>
            {t('overview.description', {
              createdAt: formatter.date.dateTime(
                new Date(data.cluster.createdAt as string)
              ),
            })}
          </CardDescription>
        </CardHeader>
      </Card>

      {isEval && <OverallScore clusterId={clusterId} />}

      <ModelRuns
        data={data}
        columns={columns}
      />
    </>
  );
};
